<script setup lang="ts">
import { computed } from 'vue'
import { AlertTriangle, Check, Info, Loader2, X, XCircle } from '@lucide/vue'
import MsButton from '../button/button.vue'
import { useToastDismiss, type ToastEntry } from './use-toast'

defineOptions({ name: 'MsToast' })

const props = defineProps<{ entry: ToastEntry }>()

// Through the host, so the exit animates.
const dismiss = useToastDismiss()

const ICONS = {
  success: Check,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
  loading: Loader2,
} as const

const icon = computed(() => ICONS[props.entry.type as keyof typeof ICONS] ?? null)

const role = computed(() => (props.entry.type === 'error' ? 'alert' : 'status'))
</script>

<template>
  <div class="ms-toast" :class="`ms-toast--${entry.type}`" :role="role">
    <component :is="icon" v-if="icon" class="ms-toast__icon" aria-hidden="true" />
    <div class="ms-toast__body">
      <p class="ms-toast__title">{{ entry.title }}</p>
      <p v-if="entry.description" class="ms-toast__description">{{ entry.description }}</p>
    </div>
    <MsButton
      v-if="entry.action"
      class="ms-toast__action"
      variant="outline"
      size="xs"
      @click="entry.action.onClick()"
    >{{ entry.action.label }}</MsButton>
    <button
      type="button"
      class="ms-toast__close"
      aria-label="Dismiss"
      @click="dismiss(entry.id)"
    >
      <X aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped lang="scss">
.ms-toast {
  display: flex;
  align-items: center;
  gap: var(--toast-gap);
  inline-size: 100%;
  padding: var(--toast-padding);
  border: 1px solid var(--toast-border);
  border-radius: var(--toast-radius);
  background: var(--toast-bg);
  box-shadow: var(--toast-shadow);
  color: var(--toast-fg);
  font-family: var(--font-family-sans);
  letter-spacing: var(--tracking-normal);
  text-align: start;

  &__icon {
    flex-shrink: 0;
    inline-size: var(--toast-icon-size);
    block-size: var(--toast-icon-size);
  }

  &__body {
    flex: 1 1 auto;
    min-inline-size: 0;
  }

  &__title {
    margin: 0;
    color: var(--toast-fg);
    font-size: var(--toast-title-font-size);
    font-weight: var(--font-weight-medium);
    line-height: var(--leading-snug);
  }

  &__description {
    margin: var(--sp-2) 0 0;
    color: var(--toast-description-fg);
    font-size: var(--toast-description-font-size);
    line-height: var(--leading-snug);
  }

  &__action {
    flex-shrink: 0;

    --btn-outline-bg: transparent;
    --btn-outline-bg-hover: var(--surface-contrast-raised);
    --btn-outline-border: color-mix(in srgb, var(--toast-fg) 30%, transparent);
    --btn-outline-fg: var(--toast-fg);
  }

  &__close {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    inline-size: var(--toast-close-size);
    block-size: var(--toast-close-size);
    padding: 0;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--toast-close-fg);
    cursor: pointer;

    &:hover {
      background: var(--toast-close-bg-hover);
      box-shadow: var(--ring-state);
      color: var(--toast-close-fg-hover);
    }

    &:focus-visible {
      outline: 2px solid currentcolor;
      outline-offset: 1px;
    }

    :deep(svg) {
      inline-size: 12px;
      block-size: 12px;
    }
  }

  &--success .ms-toast__icon {
    color: var(--toast-success-fg);
  }

  &--info .ms-toast__icon {
    color: var(--toast-info-fg);
  }

  &--warning .ms-toast__icon {
    color: var(--toast-warning-fg);
  }

  &--error .ms-toast__icon {
    color: var(--toast-error-fg);
  }

  &--loading .ms-toast__icon {
    color: var(--toast-loading-fg);
    animation: ms-toast-spin var(--duration-700) var(--ease-linear) infinite;
  }
}

@keyframes ms-toast-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ms-toast--loading .ms-toast__icon {
    animation: none;
  }
}
</style>
